import React, { FC, useState, useEffect } from 'react';
import { Card, Typography, Box, LinearProgress, TextField, Button, IconButton, Alert, CardContent, CardActions, CardMedia } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import clientService from 'src/clientService';
import axios from 'axios';
import { ProjectMediaUploadRequest, ProjectMediaUploadResponse, ProjectMediaFinalizeRequest, MediaType, ProjectMedia } from 'src/api/Api';

interface UploadPreviewCardProps {
  file: File;
  projectId: string;
  onRemove: (file: File) => void;
  onUploadComplete: (fileName: string) => void;
  onFinalize: (mediaItem: ProjectMedia) => void;
}

const UploadPreviewCard: FC<UploadPreviewCardProps> = ({ file, projectId, onRemove, onUploadComplete, onFinalize }) => {
  const [caption, setCaption] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [gcsPath, setGcsPath] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    // Create a preview URL for the file
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    // Cleanup the object URL on component unmount
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  useEffect(() => {
    const uploadFile = async () => {
      setIsUploading(true);
      setError(null);

      try {
        const mediaType = file.type.startsWith('image') ? MediaType.IMAGE : file.type.startsWith('video') ? MediaType.VIDEO : MediaType.AUDIO;

        const generateUrlResponse = await clientService.api.generateProjectMediaUploadUrlApiV1AdminProjectMediaGenerateUploadUrlPost({
          project_id: projectId,
          file_name: file.name,
          content_type: file.type,
          media_type: mediaType,
        } as ProjectMediaUploadRequest);

        const { upload_url, gcs_path } = generateUrlResponse.data as ProjectMediaUploadResponse;
        setGcsPath(gcs_path);

        await axios.put(upload_url, file, {
          headers: { 'Content-Type': file.type },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            setProgress(percentCompleted);
          },
        });

        setIsUploaded(true);
        onUploadComplete(file.name);
      } catch (err) {
        console.error('Upload failed', err);
        setError('Upload failed. Please try again.');
      } finally {
        setIsUploading(false);
      }
    };

    if (!isUploading && !isUploaded) {
        uploadFile();
    }
  }, [file, projectId, onUploadComplete, isUploading, isUploaded]);

  const handleSave = async () => {
    if (!gcsPath) return;

    try {
      const mediaType = file.type.startsWith('image') ? MediaType.IMAGE : file.type.startsWith('video') ? MediaType.VIDEO : MediaType.AUDIO;

      const response = await clientService.api.finalizeProjectMediaUploadApiV1AdminProjectMediaFinalizeUploadPost({
        project_id: projectId,
        file_name: file.name,
        gcs_path: gcsPath,
        caption: caption,
        media_type: mediaType,
      } as ProjectMediaFinalizeRequest);
      
      onFinalize(response.data);

    } catch (err) {
      console.error('Finalize upload failed', err);
      setError('Failed to save caption.');
    }
  };

  const renderPreview = () => {
    if (!previewUrl) return null;
    const fileType = file.type.split('/')[0];

    if (fileType === 'image') {
      return <CardMedia component="img" height="140" image={previewUrl} alt={file.name} />;
    } else if (fileType === 'video') {
      return <CardMedia component="video" height="140" src={previewUrl} controls />;
    } else if (fileType === 'audio') {
        return (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 140, bgcolor: 'grey.200' }}>
                <Typography variant="h6">Audio File</Typography>
                <audio src={previewUrl} controls style={{ width: '100%', marginTop: '10px' }} />
            </Box>
        );
    }
    return (
        <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200' }}>
            <Typography>Unsupported File</Typography>
        </Box>
    );
  };

  return (
    <Card sx={{ 
      backgroundColor: '#111', 
      border: '1px solid #222', 
      borderRadius: '20px', 
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        borderColor: '#10B981',
        boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
      }
    }}>
      <Box sx={{ position: 'relative' }}>
        {renderPreview()}
        {isUploaded && progress === 100 && (
          <Box sx={{ 
            position: 'absolute', 
            top: 12, 
            right: 12, 
            bgcolor: '#10B981', 
            color: '#FFF', 
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
          </Box>
        )}
      </Box>
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" sx={{ color: '#FFF', fontWeight: 600, mb: 0.5 }} noWrap title={file.name}>
          {file.name}
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          {(file.size / 1024).toFixed(1)} KB
        </Typography>

        {(isUploading || (isUploaded && progress < 100)) && (
          <Box sx={{ mt: 1, mb: 2 }}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#999' }}>Uploading...</Typography>
                <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 700 }}>{progress}%</Typography>
             </Box>
            <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                    height: 6, 
                    borderRadius: 3, 
                    bgcolor: '#222',
                    '& .MuiLinearProgress-bar': { bgcolor: '#10B981' }
                }} 
            />
          </Box>
        )}
        
        {error && (
            <Alert 
                severity="error" 
                sx={{ 
                    mt: 1, 
                    mb: 2, 
                    borderRadius: '12px', 
                    bgcolor: 'rgba(239, 68, 68, 0.1)', 
                    color: '#EF4444',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    '& .MuiAlert-icon': { color: '#EF4444' }
                }}
            >
                {error}
            </Alert>
        )}

        <TextField
          fullWidth
          placeholder="Add a descriptive caption..."
          variant="outlined"
          size="small"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          sx={{ 
            mt: 1,
            '& .MuiOutlinedInput-root': {
                backgroundColor: '#1A1A1A',
                color: '#FFF',
                borderRadius: '12px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#10B981' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
            },
          }}
          disabled={isUploading || !isUploaded}
        />
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between', gap: 2 }}>
        <IconButton 
            size="small" 
            onClick={() => onRemove(file)} 
            disabled={isUploading}
            sx={{ 
                color: '#666', 
                backgroundColor: 'rgba(255,255,255,0.05)',
                '&:hover': { color: '#EF4444', backgroundColor: 'rgba(239, 68, 68, 0.1)' }
            }}
        >
            <CloseIcon fontSize="small" />
        </IconButton>
        <Button
          variant="contained"
          size="small"
          disabled={isUploading || !isUploaded || !caption}
          onClick={handleSave}
          sx={{
            flex: 1,
            bgcolor: '#10B981',
            color: '#FFF',
            fontWeight: 700,
            borderRadius: '10px',
            textTransform: 'none',
            '&:hover': { bgcolor: '#059669' },
            '&.Mui-disabled': { bgcolor: '#222', color: '#555' }
          }}
        >
          Finalize Upload
        </Button>
      </CardActions>
    </Card>
  );
};

export default UploadPreviewCard;
